import { NodeViewRendererProps } from '@tiptap/react';
import { Mention } from '@tiptap/extension-mention';

const IMG_CDN = process.env.REACT_APP_IMAGE_CDN;

// Function to get platform icon (based on transformHtmlContent logic)
const getLinkIcon = (platform: string, url: string) => {
  if (!platform) return null;

  const iconMap: Record<string, string> = {
    youtube: 'youtube_icon.svg',
    tiktok: 'tiktok_icon_new.svg',
    instagram: 'instagram_icon.png',
    x: 'x_icon.png',
    facebook: 'facebook_icon.svg',
    reddit: 'reddit_icon.svg'
  };

  if (platform === 'custom') {
    try {
      const linkHostname = new URL(url).hostname;
      return `https://icons.duckduckgo.com/ip2/${linkHostname}.ico`;
    } catch (error) {
      return `${IMG_CDN}/icons/link_icon.svg`;
    }
  }

  return iconMap[platform] ? `${IMG_CDN}/icons/${iconMap[platform]}` : null;
};

const CustomMention = Mention.extend({
  name: 'mention',
  
  addAttributes() {
    return {
      id: {},
      label: {},
      url: {
        default: null,
      },
      platform: {
        default: null,
      },
      isExternal: {
        default: false,
      },
    };
  },

  renderHTML({ node, HTMLAttributes }) {
    // Check if this is an external mention
    const isExternal = node.attrs.isExternal || node.attrs.url;
    
    if (isExternal) {
      // This defines how external mentions are serialized to HTML (stored in your database)
      return [
        'span',
        {
          ...HTMLAttributes,
          'data-type': 'external-mention',
          'data-url': node.attrs.url,
          'data-label': node.attrs.label,
          'data-platform': node.attrs.platform || 'custom',
          class: 'external-mention',
        },
        `@${node.attrs.label}`,
      ];
    }

    // This defines how regular user mentions are serialized to HTML (stored in your database)
    return [
      'span',
      {
        ...HTMLAttributes,
        'data-type': 'mention',
        'data-id': node.attrs.id,
        'data-label': node.attrs.label,
        class: 'mention',
      },
      `@${node.attrs.label}`,
    ];
  },

  addNodeView() {
    // This defines how the node is rendered in the editor
    return ({ node }: NodeViewRendererProps) => {
      const isExternal = node.attrs.isExternal || node.attrs.url;
      
      if (isExternal) {
        // External mention rendering with platform icons
        const container = document.createElement('a');
        container.setAttribute('href', node.attrs.url || '#');
        container.setAttribute('target', '_blank');
        container.setAttribute('rel', 'noopener noreferrer');
        container.setAttribute('class', 'external-mention inline-flex items-center gap-1');
        container.style.textDecoration = 'none';
        
        // Create platform icon
        const platform = node.attrs.platform || 'custom';
        const iconUrl = getLinkIcon(platform, node.attrs.url || '');
        
        if (iconUrl) {
          const icon = document.createElement('img');
          icon.setAttribute('src', iconUrl);
          icon.setAttribute('alt', `${platform} icon`);
          icon.setAttribute('class', `size-4 ${platform === 'tiktok' || platform === 'x' ? 'rounded-full bg-white' : platform === 'custom' ? 'rounded-sm' : ''}`);
          
          // Add error handling for custom icons
          if (platform === 'custom') {
            icon.onerror = function() {
              this.onerror = null; // Prevent infinite loop
              try {
                const url = new URL(node.attrs.url || '');
                const hostname = url.hostname;
                
                // Try fallback services in order
                const services = [
                  `https://www.google.com/s2/favicons?domain_url=${hostname}`,
                  `https://favicon.yandex.net/favicon/${hostname}`,
                  `https://f1.allesedv.com/16/${hostname}`,
                  `${IMG_CDN}/icons/link_icon.svg`
                ];
                
                let currentIndex = 0;
                const tryNextService = () => {
                  if (currentIndex < services.length) {
                    this.src = services[currentIndex];
                    currentIndex++;
                    if (currentIndex < services.length) {
                      this.onerror = tryNextService.bind(this);
                    }
                  }
                };
                
                tryNextService.call(this);
              } catch (error) {
                this.src = `${IMG_CDN}/icons/link_icon.svg`;
              }
            };
          }
          
          container.appendChild(icon);
        }
        
        // Create the mention text (display name, not @username)
        const mentionText = document.createElement('span');
        mentionText.setAttribute('class', 'font-medium text-eoe-text dark:text-eoe-dark-text underline');
        mentionText.textContent = node.attrs.label; // Just the display name, no @ symbol
        container.appendChild(mentionText);
        
        return {
          dom: container,
        };
      }
      
      // Regular user mention rendering
      const dom = document.createElement('a');
      dom.setAttribute('href', `/user/${node.attrs.label}`);
      dom.setAttribute('class', 'mention');
      dom.textContent = `@${node.attrs.label}`;
      return {
        dom,
      };
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="mention"]',
        getAttrs: (dom) => {
          const element = dom as HTMLElement;
          return {
            id: element.getAttribute('data-id'),
            label: element.getAttribute('data-label'),
            isExternal: false,
          };
        },
      },
      {
        tag: 'span[data-type="external-mention"]',
        getAttrs: (dom) => {
          const element = dom as HTMLElement;
          return {
            url: element.getAttribute('data-url'),
            label: element.getAttribute('data-label'),
            platform: element.getAttribute('data-platform') || 'custom',
            isExternal: true,
          };
        },
      },
    ];
  },
});

export default CustomMention;
